import { BatchPreviewTable } from "@/components/BatchPreviewTable";
import { FileDropzone } from "@/components/FileDropzone";
import WizardStepper from "@/components/WizardStepper";
import { toaster } from "@/components/ui/toaster";
import { now } from "@/helpers/time";
import { InvoiceBatch } from "@/types/types";
import {
  Badge,
  Box,
  Button,
  Dialog,
  Grid,
  GridItem,
  HStack,
  Portal,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import _ from "lodash";
import { AlertCircle, CheckCircle, Download, Info } from "lucide-react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  BatchRow,
  buildInvoiceFile,
  duplicateIds,
  formatCop,
  invoiceColumns,
  normalizeCell,
  readInvoiceFile,
  validateRow,
} from "../helpers/invoiceBatchFile";

const STEPS = ["Subir archivo", "Revisar", "Resultado"] as const;
const PAGE = 25;

type Review = {
  fileName: string;
  hasContrato: boolean;
  rows: BatchRow[];
  order: number[];
};

const validateRows = (rows: BatchRow[]) => {
  const today = now().format("YYYY-MM-DD");
  const duplicates = duplicateIds(rows);
  return rows.map((row, index) => [
    ...validateRow(row, today),
    ...(duplicates.has(index)
      ? [{ field: "ID", message: "ID repetido en el archivo" }]
      : []),
  ]);
};

export default function UploadFileModal({
  isOpen,
  onClose,
  invoiceBatch,
  uploadFile,
  resetBatch,
}: {
  isOpen: boolean;
  onClose: () => void;
  invoiceBatch: InvoiceBatch | null;
  uploadFile: (file: File) => Promise<string>;
  resetBatch: () => void;
}) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [review, setReview] = useState<Review | null>(null);
  const [directUpload, setDirectUpload] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const activeReferenceRef = useRef<string | null>(null);

  const isCurrentBatch =
    !invoiceBatch ||
    !activeReferenceRef.current ||
    invoiceBatch.reference === activeReferenceRef.current;

  const isComplete = isCurrentBatch && invoiceBatch?.status === "complete";
  const isError = isCurrentBatch && invoiceBatch?.status === "error";
  const hasErrors = (invoiceBatch?.errors?.length ?? 0) > 0;
  const isProcessing =
    isSubmitting || (isCurrentBatch && invoiceBatch?.status === "processing");
  const showSuccess = !isProcessing && isComplete && !hasErrors;
  const showErrorResult =
    !isProcessing && ((isComplete && hasErrors) || isError);
  const showResult = showSuccess || showErrorResult;
  const step = showResult || isProcessing ? 2 : review ? 1 : 0;

  useEffect(() => {
    if (!isSubmitting || !isCurrentBatch) return;
    if (isComplete || isError) setIsSubmitting(false);
  }, [isSubmitting, isComplete, isError, isCurrentBatch]);

  const errors = useMemo(
    () => (review ? validateRows(review.rows) : []),
    [review]
  );
  const columns = useMemo(
    () => (review ? invoiceColumns(review.rows, review.hasContrato) : []),
    [review]
  );
  const errorCount = errors.filter((e) => e.length).length;
  const readyCount = errors.length - errorCount;
  const total = (review?.rows ?? []).reduce(
    (sum, row, index) =>
      errors[index].length ? sum : sum + (Number(row["Monto 1"]) || 0),
    0
  );
  const topCauses = _.sortBy(
    Object.entries(_.countBy(errors.flat(), (e) => e.message)),
    ([, count]) => -count
  ).slice(0, 4);

  const reset = () => {
    setSelectedFile(null);
    setReview(null);
    setDirectUpload(false);
    setReadError(null);
    setVisibleCount(PAGE);
  };

  const handleClose = () => {
    if (!isProcessing) {
      resetBatch();
      reset();
      setIsSubmitting(false);
      setIsUploadingFile(false);
      activeReferenceRef.current = null;
    }
    onClose();
  };

  const handleFile = async (file: File | null) => {
    reset();
    setSelectedFile(file);
    if (!file) return;

    try {
      const parsed = await readInvoiceFile(file);
      if (!parsed) return setDirectUpload(true);
      if (!parsed.rows.length)
        return setReadError("El archivo está vacío. No encontramos recaudos.");
      const rowErrors = validateRows(parsed.rows);
      const order = _.sortBy(
        parsed.rows.map((_row, index) => index),
        (index) => (rowErrors[index].length ? 0 : 1)
      );
      setReview({ fileName: file.name, ...parsed, order });
    } catch {
      setReadError("No pudimos leer el archivo. Formato incorrecto.");
    }
  };

  const updateRow = (index: number, patch: BatchRow) =>
    setReview(
      (current) =>
        current && {
          ...current,
          rows: current.rows.map((row, i) =>
            i === index ? { ...row, ...patch } : row
          ),
        }
    );

  const handleBlur = (index: number, key: string) => {
    const value = review?.rows[index][key];
    if (typeof value === "string")
      updateRow(index, { [key]: normalizeCell(key, value) });
  };

  const startUpload = async (file: File) => {
    resetBatch();
    activeReferenceRef.current = null;
    setIsSubmitting(true);
    setIsUploadingFile(true);
    try {
      activeReferenceRef.current = await uploadFile(file);
    } catch {
      setIsSubmitting(false);
      activeReferenceRef.current = null;
      toaster.create({
        title: "Error",
        description: "Hubo un error al subir el archivo de recaudos.",
        type: "error",
        duration: 5000,
      });
    } finally {
      setIsUploadingFile(false);
    }
  };

  const uploadReady = () => {
    if (!review) return;
    const rows = review.rows.filter((_row, index) => !errors[index].length);
    startUpload(buildInvoiceFile(rows, review.hasContrato, review.fileName));
  };

  return (
    <Dialog.Root
      lazyMount
      open={isOpen}
      onOpenChange={(e) => !e.open && handleClose()}
      size={step === 1 ? "xl" : "lg"}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            maxHeight="85vh"
            display="flex"
            flexDirection="column"
          >
            <Dialog.Header
              borderBottom="1px solid #ebecef80"
              p={4}
              flexDirection="column"
              alignItems="stretch"
              gap={3}
            >
              <HStack justify="space-between" align="center" w="full" pr={8}>
                <Box>
                  <Dialog.Title fontSize="lg" fontWeight="semibold">
                    Carga masiva de recaudos
                  </Dialog.Title>
                  <Text fontSize="sm" color="gray.500">
                    Paso {step + 1} de {STEPS.length}
                  </Text>
                </Box>
                {step === 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const link = document.createElement("a");
                      link.href = "/recaudos_plantilla.xlsx";
                      link.download = "recaudos_plantilla.xlsx";
                      link.click();
                    }}
                  >
                    <Download size={16} />
                    Descargar plantilla
                  </Button>
                )}
              </HStack>
              <WizardStepper
                steps={STEPS}
                current={step}
                label="Pasos de la carga"
              />
            </Dialog.Header>
            <Dialog.CloseTrigger position="top-end" top={3} />
            <Dialog.Body flex="1" overflowY="auto" py={6}>
              {isProcessing ? (
                <VStack gap={4} py={8}>
                  <Spinner size="lg" color="palomma.500" />
                  <Text fontWeight="medium" color="gray.700">
                    {isUploadingFile
                      ? "Subiendo archivo..."
                      : "Procesando archivo..."}
                  </Text>
                  {invoiceBatch?.totalCount != null ? (
                    <Text fontSize="sm" color="gray.500">
                      {invoiceBatch.successCount ?? 0} de{" "}
                      {invoiceBatch.totalCount} recaudos procesados
                    </Text>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      Esto puede tomar unos segundos
                    </Text>
                  )}
                </VStack>
              ) : showSuccess ? (
                <VStack gap={4} align="stretch" py={2}>
                  <VStack gap={3}>
                    <Box p={4} bg="green.50" borderRadius="full">
                      <CheckCircle size={40} color="#38a169" />
                    </Box>
                    <Text fontWeight="medium" color="green.600">
                      Todos los recaudos se han creado exitosamente
                    </Text>
                  </VStack>
                  <HStack gap={6} px={1}>
                    <HStack>
                      <Text fontSize="sm" fontWeight={600}>
                        Total de registros:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {invoiceBatch?.totalCount ?? 0}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" fontWeight={600}>
                        Creados:
                      </Text>
                      <Text fontSize="sm" color="green.600">
                        {invoiceBatch?.successCount ?? 0}
                      </Text>
                    </HStack>
                  </HStack>
                </VStack>
              ) : showErrorResult ? (
                <VStack gap={4} align="stretch">
                  <VStack gap={3} py={2}>
                    <Box p={4} bg="red.50" borderRadius="full">
                      <AlertCircle size={40} color="#e53e3e" />
                    </Box>
                    <Text fontWeight="medium" color="red.600">
                      {isError
                        ? "Error al procesar el archivo"
                        : "El archivo se procesó con errores"}
                    </Text>
                  </VStack>

                  {invoiceBatch?.batchError && (
                    <Box w="full" p={4} bg="red.50" borderRadius="md">
                      <Text fontSize="sm" color="gray.700">
                        {invoiceBatch.batchError}
                      </Text>
                    </Box>
                  )}

                  {isComplete && (
                    <HStack gap={6} px={1}>
                      <HStack>
                        <Text fontSize="sm" fontWeight={600}>
                          Creados:
                        </Text>
                        <Text fontSize="sm" color="green.600">
                          {invoiceBatch?.successCount ?? 0}
                        </Text>
                      </HStack>
                      <HStack>
                        <Text fontSize="sm" fontWeight={600}>
                          Errores:
                        </Text>
                        <Text fontSize="sm" color="red.600">
                          {invoiceBatch?.errors?.length ?? 0}
                        </Text>
                      </HStack>
                    </HStack>
                  )}

                  {hasErrors && (
                    <Box w="full" overflowY="auto" maxHeight="280px">
                      <Grid templateColumns="repeat(2, 1fr)">
                        <GridItem
                          fontWeight="bold"
                          fontSize="sm"
                          borderBottom="1px solid #ebecef80"
                          p="0.5rem"
                          bg="#F7F9FC"
                        >
                          Cédula
                        </GridItem>
                        <GridItem
                          fontWeight="bold"
                          fontSize="sm"
                          borderBottom="1px solid #ebecef80"
                          p="0.5rem"
                          bg="#F7F9FC"
                        >
                          Error
                        </GridItem>
                        {invoiceBatch?.errors?.map((error, idx) => (
                          <Fragment key={idx}>
                            <GridItem
                              fontSize="sm"
                              borderBottom="1px solid #ebecef80"
                              p="0.5rem"
                            >
                              {error.documentNumber}
                            </GridItem>
                            <GridItem
                              fontSize="sm"
                              color="red.700"
                              borderBottom="1px solid #ebecef80"
                              p="0.5rem"
                            >
                              {error.errorMessage}
                            </GridItem>
                          </Fragment>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </VStack>
              ) : review ? (
                <VStack align="stretch" gap={3}>
                  <Box>
                    <Text fontWeight="semibold">
                      Leímos tu archivo: {review.rows.length} recaudos
                      {errorCount > 0 && `, ${errorCount} con error`}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Corrige aquí mismo, no hace falta volver al Excel. Los que
                      ya están bien se cargan igual.
                    </Text>
                  </Box>
                  <HStack gap={2} flexWrap="wrap">
                    <Badge colorPalette="green" variant="subtle" size="md">
                      {readyCount} listos
                    </Badge>
                    {errorCount > 0 && (
                      <Badge colorPalette="red" variant="subtle" size="md">
                        {errorCount} con error
                      </Badge>
                    )}
                    <Badge colorPalette="gray" variant="subtle" size="md">
                      {review.fileName} · Total {formatCop(total)}
                    </Badge>
                  </HStack>
                  {topCauses.length > 0 && (
                    <Text fontSize="xs" color="gray.500">
                      Causas:{" "}
                      {topCauses
                        .map(([cause, count]) => `${cause} (${count})`)
                        .join(" · ")}
                    </Text>
                  )}
                  <BatchPreviewTable
                    columns={columns}
                    rows={review.rows}
                    order={review.order.slice(0, visibleCount)}
                    errors={errors}
                    onEdit={(index, key, value) =>
                      updateRow(index, { [key]: value })
                    }
                    onBlur={handleBlur}
                  />
                  <HStack justify="space-between">
                    <Text fontSize="xs" color="gray.500">
                      Mostrando {Math.min(visibleCount, review.rows.length)} de{" "}
                      {review.rows.length} · los pendientes aparecen primero
                    </Text>
                    {visibleCount < review.rows.length && (
                      <Button
                        variant="plain"
                        size="xs"
                        color="palomma.500"
                        onClick={() => setVisibleCount((c) => c + PAGE)}
                      >
                        Mostrar {PAGE} más
                      </Button>
                    )}
                  </HStack>
                  <HStack
                    gap={2}
                    p={3}
                    bg="palomma.50"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="palomma.100"
                  >
                    <Info size={16} color="#4012AB" />
                    <Text fontSize="sm" color="palomma.700">
                      Los inquilinos se crean solos con cada recaudo. No tienes
                      que cargarlos aparte en «Clientes».
                    </Text>
                  </HStack>
                </VStack>
              ) : (
                <VStack align="stretch" gap={3}>
                  <FileDropzone
                    file={selectedFile}
                    onChange={handleFile}
                    accept=".txt,.csv,.xlsx,.xls"
                    hint="Soporta archivos .TXT, .CSV, .XLSX y .XLS"
                  />
                  {readError && (
                    <Text fontSize="sm" color="red.600">
                      {readError}
                    </Text>
                  )}
                </VStack>
              )}
            </Dialog.Body>
            <Dialog.Footer borderTop="1px solid #ebecef80" p={4}>
              {step === 1 ? (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => handleFile(null)}
                    mr="auto"
                  >
                    Subir otro archivo
                  </Button>
                  <Button
                    colorPalette="palomma"
                    onClick={uploadReady}
                    disabled={readyCount === 0}
                  >
                    {errorCount > 0
                      ? `Cargar solo los ${readyCount} listos`
                      : `Cargar los ${readyCount} recaudos`}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={handleClose}
                    disabled={isProcessing}
                  >
                    {showResult ? "Cerrar" : "Cancelar"}
                  </Button>
                  {step === 0 && directUpload && selectedFile && (
                    <Button
                      colorPalette="palomma"
                      onClick={() => startUpload(selectedFile)}
                      loading={isSubmitting}
                    >
                      Cargar recaudos
                    </Button>
                  )}
                </>
              )}
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
